import React, { useCallback, useEffect, useState } from 'react';
import { addDoc, collection, deleteDoc, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { FiEdit2, FiMessageCircle, FiTrash2 } from 'react-icons/fi';
import { db } from '../../lib/firebase';
import { formatDateTime } from '../../lib/format';
import { BUTTON, EmptyState, Panel, SIZE, SkeletonRows, TextArea, Notice } from '../ui/kit';

// Progress notes of a file ("Mises à jour"), stored in Firestore "commentaires" as before.
const PatientNotes = ({ patient, user, canWrite }) => {
  const [notes, setNotes] = useState([]);
  const [status, setStatus] = useState('loading');
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const snapshot = await getDocs(query(collection(db, 'commentaires'), where('patientId', '==', patient._id)));
      const items = snapshot.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .sort((a, b) => new Date(b.dateCreation || 0) - new Date(a.dateCreation || 0));
      setNotes(items);
      setStatus('ready');
    } catch (err) {
      setStatus('error');
    }
  }, [patient._id]);

  useEffect(() => {
    load();
  }, [load]);

  const cancelEdit = () => {
    setEditingId(null);
    setDraft('');
  };

  const submit = async (event) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    setError('');
    try {
      if (editingId) {
        await updateDoc(doc(db, 'commentaires', editingId), {
          texte: text,
          horodatage: new Date().toLocaleString(),
          dateMiseAJour: new Date().toISOString(),
        });
      } else {
        await addDoc(collection(db, 'commentaires'), {
          texte: text,
          horodatage: new Date().toLocaleString(),
          patientId: patient._id,
          auteur: user.name || 'Anonyme',
          dateCreation: new Date().toISOString(),
          service: user.service || 'Non spécifié',
        });
      }
      cancelEdit();
      await load();
    } catch (err) {
      setError('La mise à jour n’a pas pu être enregistrée. Réessayez.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (note) => {
    if (!window.confirm('Supprimer cette mise à jour ?')) return;
    try {
      await deleteDoc(doc(db, 'commentaires', note.id));
      await load();
    } catch (err) {
      setError('La mise à jour n’a pas pu être supprimée.');
    }
  };

  return (
    <Panel title="Mises à jour" description="Évolution du patient, notée par l’équipe.">
      {canWrite && (
        <form onSubmit={submit} className="mb-[20px] print:hidden">
          <TextArea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={editingId ? 'Modifiez votre mise à jour…' : 'Nouvelle mise à jour…'}
            aria-label="Texte de la mise à jour"
          />
          <div className="mt-2 flex flex-wrap justify-end gap-2">
            {editingId && (
              <button type="button" onClick={cancelEdit} className={`${BUTTON.secondary} ${SIZE.sm}`}>
                Annuler
              </button>
            )}
            <button type="submit" disabled={!draft.trim() || busy} className={`${BUTTON.primary} ${SIZE.sm}`}>
              {busy ? 'Enregistrement…' : editingId ? 'Mettre à jour' : 'Publier'}
            </button>
          </div>
        </form>
      )}
      {error && (
        <div className="mb-[12px]">
          <Notice tone="red">{error}</Notice>
        </div>
      )}
      {status === 'loading' ? (
        <SkeletonRows rows={2} />
      ) : status === 'error' ? (
        <Notice tone="amber">Les mises à jour n’ont pas pu être chargées.</Notice>
      ) : notes.length === 0 ? (
        <EmptyState icon={FiMessageCircle} title="Aucune mise à jour">
          {canWrite ? 'La première note apparaîtra ici.' : 'L’équipe n’a pas encore noté d’évolution.'}
        </EmptyState>
      ) : (
        <ol className="m-0 flex list-none flex-col gap-[16px] p-0">
          {notes.map((note) => (
            <li key={note.id} className="relative border-0 border-l-[2px] border-solid border-brand-600/20 pl-[14px]">
              <div className="flex flex-wrap items-baseline justify-between gap-x-2">
                <span className="text-[13.5px] font-semibold text-ink">
                  {note.auteur}
                  {note.service && <span className="font-normal text-black/40"> · {note.service}</span>}
                </span>
                <span className="text-[12px] text-black/40">
                  {formatDateTime(note.dateCreation) || note.horodatage}
                  {note.dateMiseAJour && ' · modifiée'}
                </span>
              </div>
              <p className="mb-0 mt-1 whitespace-pre-wrap break-words text-[14px] leading-relaxed text-black/70">{note.texte}</p>
              {canWrite && note.auteur === user.name && (
                <div className="mt-1.5 flex gap-1 print:hidden">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(note.id);
                      setDraft(note.texte || '');
                    }}
                    className={`${BUTTON.ghost} h-8 px-[10px] text-[12.5px]`}
                  >
                    <FiEdit2 size={12} aria-hidden="true" />
                    Modifier
                  </button>
                  <button type="button" onClick={() => remove(note)} className={`${BUTTON.ghost} h-8 px-[10px] text-[12.5px] text-red-600 hover:bg-red-50 hover:text-red-700`}>
                    <FiTrash2 size={12} aria-hidden="true" />
                    Supprimer
                  </button>
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
};

export default PatientNotes;
